import React, { useState, useEffect, useRef } from 'react';
import './CSVUpload.css';

const CSVUpload = ({ onUpload, onCalculateScores, loading, onClear }) => {
  const [file, setFile] = useState(null);
  const [error, setError] = useState('');
  const [preview, setPreview] = useState(null);
  const [showBuilder, setShowBuilder] = useState(false);
  const [builderRows, setBuilderRows] = useState([{ symbol: '', shares: '', entry_price: '' }]);
  const fileInputRef = useRef(null);

  // Listen for load-watchlist settings action
  const propsRef = useRef({ onUpload, onCalculateScores, loading });
  propsRef.current = { onUpload, onCalculateScores, loading };
  useEffect(() => {
    const handler = (e) => {
      if (e.detail !== 'load-watchlist') return;
      const raw = localStorage.getItem('alphagrade_watchlist');
      if (!raw) return;
      try {
        const watchlist = JSON.parse(raw);
        if (!watchlist?.length) return;
        const items = watchlist.map(w => ({
          symbol: w.symbol,
          shares: 1,
          entry_price: w.price || 0,
          current_price: null,
        }));
        setPreview(items);
        propsRef.current.onUpload(items);
        propsRef.current.onCalculateScores(items);
      } catch (err) {
        console.error('Failed to load watchlist:', err);
      }
    };
    window.addEventListener('settings-action', handler);
    return () => window.removeEventListener('settings-action', handler);
  }, []);

  const normalizeColumnName = (name) => {
    if (!name) return '';
    return String(name).toLowerCase().trim().replace(/[^a-z0-9]/g, '');
  };

  const findColumnIndex = (headers, possibleNames) => {
    for (let i = 0; i < headers.length; i++) {
      const normalized = normalizeColumnName(headers[i]);
      for (const name of possibleNames) {
        if (normalized === normalizeColumnName(name) || normalized.includes(normalizeColumnName(name))) {
          return i;
        }
      }
    }
    return -1;
  };

  const parseCSV = (text) => {
    const lines = text.trim().split('\n');
    const headers = lines[0].split(',').map(h => h.trim());
    
    const symbolIdx = findColumnIndex(headers, ['symbol', 'ticker', 'sym', 'stock']);
    const sharesIdx = findColumnIndex(headers, ['shares', 'quantity', 'qty', 'amount', 'units', 'coins', 'coin', 'tokens', 'holdings']);
    const entryPriceIdx = findColumnIndex(headers, ['entry', 'cost', 'price', 'basis', 'avg', 'average', 'entryprice', 'avgprice', 'avgshareprice', 'costbasis']);
    const currentPriceIdx = findColumnIndex(headers, ['current', 'market', 'last', 'close', 'currentprice']);

    if (symbolIdx === -1 || sharesIdx === -1) {
      return [];
    }

    const items = [];
    for (let i = 1; i < lines.length; i++) {
      const values = lines[i].split(',').map(v => v.trim());
      
      const symbol = String(values[symbolIdx] || '').toUpperCase().trim();
      const shares = parseFloat(values[sharesIdx]) || 0;
      const entryPrice = entryPriceIdx !== -1 ? (parseFloat(values[entryPriceIdx]) || 0) : 0;
      const currentPrice = currentPriceIdx !== -1 ? (parseFloat(values[currentPriceIdx]) || null) : null;

      if (symbol && symbol.length > 0 && symbol.length <= 10 && shares > 0) {
        items.push({
          symbol,
          shares,
          entry_price: entryPrice,
          current_price: currentPrice,
        });
      }
    }
    return items;
  };

  // SheetJS is ~800KB and was a top-level import, so it shipped in the main
  // bundle to every visitor, including anonymous ad clicks who never open a
  // spreadsheet. Loaded on demand instead: the only two places that need it
  // are parsing an uploaded .xlsx and building the .xlsx template.
  // Interop guard: depending on how the package resolves, the namespace either
  // carries the API directly or hangs it off .default.
  const loadXLSX = async () => {
    const mod = await import('xlsx');
    return mod.read ? mod : (mod.default || mod);
  };

  const parseExcel = async (arrayBuffer) => {
    const XLSX = await loadXLSX();
    const workbook = XLSX.read(arrayBuffer, { type: 'array' });
    const sheetName = workbook.SheetNames[0];
    const worksheet = workbook.Sheets[sheetName];
    
    // Get raw data as array of arrays to handle headers anywhere
    const rawData = XLSX.utils.sheet_to_json(worksheet, { header: 1, defval: '' });
    
    if (rawData.length < 2) {
      return [];
    }

    // Find the header row by looking for a row with "symbol" or "ticker" in it
    let headerRowIndex = -1;
    const symbolNames = ['symbol', 'ticker', 'sym', 'stock'];
    
    for (let i = 0; i < Math.min(rawData.length, 10); i++) {
      const row = rawData[i];
      for (const cell of row) {
        const normalized = normalizeColumnName(cell);
        if (symbolNames.some(name => normalized.includes(name))) {
          headerRowIndex = i;
          break;
        }
      }
      if (headerRowIndex !== -1) break;
    }

    // If no header row found, assume first row is header
    if (headerRowIndex === -1) {
      headerRowIndex = 0;
    }

    const headers = rawData[headerRowIndex];
    
    // Find column indices
    const symbolIdx = findColumnIndex(headers, ['symbol', 'ticker', 'sym', 'stock']);
    const sharesIdx = findColumnIndex(headers, ['shares', 'quantity', 'qty', 'amount', 'units', 'coins', 'coin', 'tokens', 'holdings']);
    const entryPriceIdx = findColumnIndex(headers, ['entry', 'cost', 'price', 'basis', 'avg', 'average', 'entryprice', 'avgprice', 'avgshareprice', 'costbasis']);
    const currentPriceIdx = findColumnIndex(headers, ['current', 'market', 'last', 'close', 'currentprice']);

    console.log('Detected columns:', { 
      headers, 
      headerRowIndex,
      symbolIdx, 
      sharesIdx, 
      entryPriceIdx, 
      currentPriceIdx 
    });

    if (symbolIdx === -1 || sharesIdx === -1) {
      console.error('Could not find required columns. Headers:', headers);
      return [];
    }

    const items = [];
    for (let i = headerRowIndex + 1; i < rawData.length; i++) {
      const row = rawData[i];
      
      const symbol = String(row[symbolIdx] || '').toUpperCase().trim();
      const shares = parseFloat(row[sharesIdx]) || 0;
      const entryPrice = entryPriceIdx !== -1 ? (parseFloat(row[entryPriceIdx]) || 0) : 0;
      const currentPrice = currentPriceIdx !== -1 ? (parseFloat(row[currentPriceIdx]) || null) : null;

      if (symbol && symbol.length > 0 && symbol.length <= 10 && shares > 0) {
        items.push({
          symbol,
          shares,
          entry_price: entryPrice,
          current_price: currentPrice,
        });
      }
    }
    
    return items;
  };

  const handleFileChange = async (e) => {
    const selectedFile = e.target.files[0];
    setError('');
    setPreview(null);
    
    if (!selectedFile) {
      setFile(null);
      return;
    }

    const isCSV = selectedFile.type === 'text/csv' || selectedFile.name.endsWith('.csv');
    const isExcel = selectedFile.name.endsWith('.xlsx') || selectedFile.name.endsWith('.xls') ||
                    selectedFile.type === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' ||
                    selectedFile.type === 'application/vnd.ms-excel';

    if (!isCSV && !isExcel) {
      setError('Please select a CSV or Excel file (.csv, .xlsx, .xls)');
      setFile(null);
      return;
    }

    setFile(selectedFile);

    // Parse and preview the file
    try {
      let items;
      if (isCSV) {
        const text = await selectedFile.text();
        items = parseCSV(text);
      } else {
        const arrayBuffer = await selectedFile.arrayBuffer();
        items = await parseExcel(arrayBuffer);
      }
      
      if (items.length === 0) {
        setError('No valid holdings found. Please make sure your file matches the template format (download below).');
        setFile(null);
      } else {
        setPreview(items);
      }
    } catch (err) {
      console.error('Parse error:', err);
      setError('Error reading file. Please check the format or use the template.');
      setFile(null);
    }
  };

  const handleUpload = async () => {
    if (!preview || preview.length === 0) {
      setError('No valid data to upload');
      return;
    }

    try {
      onUpload(preview);
      onCalculateScores(preview);
    } catch (err) {
      setError(err.message || 'Error processing file');
      console.error('Upload error:', err);
    }
  };

  const downloadTemplate = async (format) => {
    const templateData = [
      ['Symbol', 'Shares', 'Entry Price'],
      ['AAPL', 100, 150.00],
      ['NVDA', 50, 200.00],
      ['MSFT', 75, 300.00],
    ];

    if (format === 'xlsx') {
      const XLSX = await loadXLSX();
      const ws = XLSX.utils.aoa_to_sheet(templateData);
      // Set column widths
      ws['!cols'] = [{ wch: 12 }, { wch: 12 }, { wch: 14 }];
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Portfolio');
      XLSX.writeFile(wb, 'portfolio_template.xlsx');
    } else {
      const csvContent = templateData.map(row => row.join(',')).join('\n');
      const blob = new Blob([csvContent], { type: 'text/csv' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'portfolio_template.csv';
      a.click();
      URL.revokeObjectURL(url);
    }
  };

  return (
    <div className="card">
      <h2>Upload Portfolio</h2>
      
      <div className="template-section">
        <p className="template-intro">
          <strong>First time?</strong> Try a demo or download a template:
        </p>
        <div className="template-buttons">
          <button
            className="template-btn demo-btn"
            onClick={() => {
              const demo = [
                { symbol: 'AAPL', shares: 50, entry_price: 178.00, current_price: null },
                { symbol: 'NVDA', shares: 30, entry_price: 480.00, current_price: null },
                { symbol: 'MSFT', shares: 25, entry_price: 370.00, current_price: null },
                { symbol: 'GOOGL', shares: 20, entry_price: 140.00, current_price: null },
                { symbol: 'AMZN', shares: 15, entry_price: 155.00, current_price: null },
                { symbol: 'TSLA', shares: 40, entry_price: 245.00, current_price: null },
                { symbol: 'META', shares: 18, entry_price: 490.00, current_price: null },
              ];
              setPreview(demo);
              onUpload(demo);
              onCalculateScores(demo);
            }}
            disabled={loading}
          >
            Try Demo Portfolio
          </button>
          <button
            className="template-btn"
            onClick={() => downloadTemplate('xlsx')}
          >
            Excel Template
          </button>
          <button
            className="template-btn"
            onClick={() => downloadTemplate('csv')}
          >
            CSV Template
          </button>
        </div>
      </div>

      <div className="upload-container">
        <div className="file-input-wrapper">
          <input
            ref={fileInputRef}
            type="file"
            id="portfolio-file"
            accept=".csv,.xlsx,.xls"
            onChange={handleFileChange}
            disabled={loading}
            className="file-input"
          />
          <label htmlFor="portfolio-file" className="file-label">
            {file ? file.name : 'Choose Your File'}
          </label>
        </div>
        
        <button
          onClick={handleUpload}
          disabled={!preview || loading}
          className="btn btn-primary"
        >
          {loading ? 'Processing...' : 'Analyze Portfolio'}
        </button>
      </div>

      <div className="builder-actions">
        <button
          className="template-btn"
          onClick={() => setShowBuilder(!showBuilder)}
          disabled={loading}
        >
          {showBuilder ? 'Hide Builder' : 'Build Portfolio Manually'}
        </button>
        {onClear && (
          <button
            className="template-btn clear-btn"
            onClick={() => {
              onClear();
              setPreview(null);
              setFile(null);
              setError('');
              setBuilderRows([{ symbol: '', shares: '', entry_price: '' }]);
              setShowBuilder(false);
              if (fileInputRef.current) fileInputRef.current.value = '';
            }}
            disabled={loading}
          >
            Clear Portfolio
          </button>
        )}
      </div>

      {showBuilder && (
        <div className="portfolio-builder">
          <h4 className="builder-title">Build a Portfolio</h4>
          <p className="builder-desc">Enter tickers and shares. Entry price is optional — leave blank and the current market price will be used.</p>
          <div className="builder-rows">
            {builderRows.map((row, idx) => (
              <div key={idx} className="builder-row">
                <input
                  className="builder-input builder-symbol"
                  placeholder="Ticker"
                  value={row.symbol}
                  onChange={(e) => {
                    const updated = [...builderRows];
                    updated[idx].symbol = e.target.value.toUpperCase();
                    setBuilderRows(updated);
                  }}
                  disabled={loading}
                />
                <input
                  className="builder-input builder-shares"
                  placeholder="Shares"
                  type="number"
                  min="0"
                  value={row.shares}
                  onChange={(e) => {
                    const updated = [...builderRows];
                    updated[idx].shares = e.target.value;
                    setBuilderRows(updated);
                  }}
                  disabled={loading}
                />
                <input
                  className="builder-input builder-price"
                  placeholder="Entry $ (optional)"
                  type="number"
                  min="0"
                  step="0.01"
                  value={row.entry_price}
                  onChange={(e) => {
                    const updated = [...builderRows];
                    updated[idx].entry_price = e.target.value;
                    setBuilderRows(updated);
                  }}
                  disabled={loading}
                />
                <button
                  className="builder-remove"
                  onClick={() => {
                    if (builderRows.length > 1) {
                      setBuilderRows(builderRows.filter((_, i) => i !== idx));
                    }
                  }}
                  disabled={loading || builderRows.length <= 1}
                  title="Remove"
                >
                  ×
                </button>
              </div>
            ))}
          </div>
          <div className="builder-controls">
            <button
              className="template-btn"
              onClick={() => setBuilderRows([...builderRows, { symbol: '', shares: '', entry_price: '' }])}
              disabled={loading}
            >
              + Add Row
            </button>
            <button
              className="btn btn-primary"
              disabled={loading || !builderRows.some(r => r.symbol.trim() && r.shares)}
              onClick={() => {
                const items = builderRows
                  .filter(r => r.symbol.trim() && parseFloat(r.shares) > 0)
                  .map(r => ({
                    symbol: r.symbol.trim().toUpperCase(),
                    shares: parseFloat(r.shares),
                    entry_price: r.entry_price ? parseFloat(r.entry_price) : 0,
                    current_price: null,
                  }));
                if (items.length > 0) {
                  setPreview(items);
                  onUpload(items);
                  onCalculateScores(items);
                  setShowBuilder(false);
                }
              }}
            >
              {loading ? 'Analyzing...' : 'Analyze Portfolio'}
            </button>
          </div>
        </div>
      )}

      {error && <div className="error-message">{error}</div>}

      {preview && preview.length > 0 && (
        <div className="preview-section">
          <p className="preview-header">
            ✓ Found {preview.length} holding{preview.length !== 1 ? 's' : ''}
          </p>
          <div className="preview-table-wrapper">
            <table className="preview-table">
              <thead>
                <tr>
                  <th>Symbol</th>
                  <th>Shares</th>
                  <th>Entry Price</th>
                </tr>
              </thead>
              <tbody>
                {preview.slice(0, 5).map((item, idx) => (
                  <tr key={idx}>
                    <td><strong>{item.symbol}</strong></td>
                    <td>{item.shares.toLocaleString()}</td>
                    <td>${item.entry_price.toFixed(2)}</td>
                  </tr>
                ))}
                {preview.length > 5 && (
                  <tr className="preview-more">
                    <td colSpan={3}>...and {preview.length - 5} more</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <div className="upload-info">
        <p><strong>File Format Requirements:</strong></p>
        <div className="format-example">
          <table className="example-table">
            <thead>
              <tr>
                <th>Symbol</th>
                <th>Shares</th>
                <th>Entry Price</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>AAPL</td>
                <td>100</td>
                <td>150.00</td>
              </tr>
              <tr>
                <td>NVDA</td>
                <td>50</td>
                <td>200.00</td>
              </tr>
            </tbody>
          </table>
        </div>
        <p className="format-notes">
          <strong>Tips:</strong> Headers must be in row 1. Column names are flexible 
          (e.g., "Ticker" works for Symbol, "Quantity" works for Shares, "Cost" works for Entry Price).
        </p>
      </div>
    </div>
  );
};

export default CSVUpload;
